import { Alert, Autocomplete, Button, CircularProgress, Switch, TextField, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { colorOrange } from "../../../config/color";
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { urlAPI } from "../../../config/config";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/reducers";
import { ITurnover } from "../../../interface/turnover";
import { sessionEnd } from "../../../redux/actions";
import { IClient } from "../../../interface/client";
import { getAll } from "../../../fetchs/get";
import TurnoverCollabField from "./TurnoverCollabField";

const TurnoverInfosFields = ({
  turnover,
  formik,
  setTjm
}: {
  turnover?: ITurnover,
  formik: any,
  setTjm: Function
}) => {
  const dispatch = useDispatch();
  const token = useSelector((state: RootState) => state.user.token);

  const [message, setMessage] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [salaries, setSalaries] = useState<any>([]);
  const [clients, setClients] = useState<IClient[]>([]);
  const [projects, setProjects] = useState<any>([]);
  const [isRealProject, setIsRealProject] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);

  // 1. Initialisation du mode (Réel vs Fictif) si on est en édition
  useEffect(() => {
    if (turnover) {
      // Si on a un collab fictif ou si le projet ne ressemble pas à une ref Dolibarr
      const isFictif = !!turnover.fictifCollab || (turnover.project && !turnover.project.includes("-"));
      setIsRealProject(!isFictif);
    }
  }, [turnover]);

  // 2. Chargement des clients
  const getAllClients = useCallback(() => {
    if (token) {
      getAll(token, "/clients/", setClients, () => dispatch(sessionEnd()), setError);
    }
  }, [token, dispatch]);

  useEffect(() => {
    getAllClients();
  }, [getAllClients]);

  // 3. Chargement des salariés (pour la Régie)
  const fetchSalaries = useCallback(async () => {
    try {
      const response = await axios.get(`${urlAPI}/salary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSalaries(response.data.data);
    } catch (err) {
      setError("Erreur lors de la récupération des salariés");
    }
  }, [token]);

  useEffect(() => {
    fetchSalaries();
  }, [fetchSalaries]);

  // 4. Chargement des projets Dolibarr au changement de client
const fetchProjects = async (dolibarrId: string) => {
  setLoading(true);
  try {
    const response = await axios.get(`${urlAPI}/projects/getProjectsDolibarrByClient/${dolibarrId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const rawList = response.data?.message;
    const list = Array.isArray(rawList) ? rawList : [];
    setProjects(list);
  } catch (err) {
    setProjects([]);
  } finally {
    setLoading(false);
  }
};

useEffect(() => {
  if (formik.values.client && isRealProject && clients.length > 0) {
    const client = clients.find((c) => c._id === formik.values.client);
    if (client?.dolibarr_id) {
      fetchProjects(client.dolibarr_id);
    } else {
    }
  }
}, [formik.values.client, isRealProject, clients]);

  // 5. Handlers de changement sécurisés
  const handleClientChange = (event: any, value: any) => {
    formik.setFieldValue("client", value?._id || "");
    formik.setFieldValue("project", ""); // Reset projet si client change
    setProjects([]);
  };

  const handleTypeChange = (event: any, value: any) => {
    formik.setFieldValue("type", value || "");
    formik.setFieldValue("project", "");
    formik.setFieldValue("collab", "");
    formik.setFieldValue("fictifCollab", "");
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {/* TYPE */}
      <Autocomplete
        fullWidth
        options={["Forfait", "Hébergement", "Régie", "TMA/TME", "TRA"]}
        value={formik.values.type}
        onChange={handleTypeChange}
        renderInput={(params) => <TextField {...params} label="Type de prestation" required error={formik.touched.type && !!formik.errors.type} />}
      />

      {/* AGENCE */}
      <Autocomplete
        fullWidth
        options={["Toutes", "Nord", "Sud"]}
        value={formik.values.agency}
        onChange={(e, v) => formik.setFieldValue("agency", v || "Toutes")}
        renderInput={(params) => <TextField {...params} label="Agence" />}
      />

      {/* CLIENT */}
      <Autocomplete
        fullWidth
        options={clients}
        getOptionLabel={(option: IClient) => option.name || ""}
        isOptionEqualToValue={(option, value) => option._id === value?._id}
        value={clients.find((c) => c._id === formik.values.client) || null}
        onChange={handleClientChange}
        renderInput={(params) => <TextField {...params} label="Client" required error={formik.touched.client && !!formik.errors.client} />}
      />

      {/* PROJET / COLLAB LOGIQUE */}
      {formik.values.type === "Régie" ? (
        <TurnoverCollabField formik={formik} salaries={salaries} value={salaries.find((s: any) => s._id === formik.values.collab) || null} setTjm={setTjm} />
      ) : (
        <>
          <Box sx={{ 
              display: "flex", 
              alignItems: "center", 
              justifyContent: "center", 
              gap: 2, 
              bgcolor: "#f1f5f9",
              borderRadius: 10 // Look pilule
          }}>
              <Typography variant="body2">Fictif</Typography>
              <Switch checked={isRealProject} onChange={() => setIsRealProject(!isRealProject)} disabled={!formik.values.client} />
              <Typography variant="body2">Réel (Dolibarr)</Typography>
          </Box>

          {isRealProject ? (
            <Autocomplete
              fullWidth
              disabled={!formik.values.client}
              options={Array.isArray(projects) ? projects : []}
              getOptionLabel={(option: any) =>
                `${option.syncMetadata?.ref ?? ""} - ${option.syncMetadata?.title ?? option.name ?? ""}`
              }
              value={
                (Array.isArray(projects) ? projects : []).find(
                  (p: any) =>
                    `${p.syncMetadata?.ref ?? ""} - ${p.syncMetadata?.title ?? p.name ?? ""}` === formik.values.project
                ) || null
              }
              onChange={(e, v) =>
                formik.setFieldValue(
                  "project",
                  v ? `${v.syncMetadata?.ref ?? ""} - ${v.syncMetadata?.title ?? v.name ?? ""}` : ""
                )
              }
              renderInput={(params) => <TextField {...params} label="Projet associé" required />}
            />
          ) : (
            <TextField
              label="Nom du projet fictif"
              value={formik.values.project}
              onChange={(e) => formik.setFieldValue("project", e.target.value)}
              required
              fullWidth
            />
          )}
        </>
      )}

      {/* FEEDBACK & SYNC */}
      {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mt: 1 }}>{message}</Alert>}
      
      {loading && <CircularProgress size={24} sx={{ alignSelf: "center", my: 1 }} />}

      <Button
        onClick={async () => {
            setLoading(true);
            setError("");
            try {
                const [clientsRes] = await Promise.all([
                  axios.get(`${urlAPI}/clients/dolibarr`, { headers: { Authorization: `Bearer ${token}` } }),
                  axios.get(`${urlAPI}/projects/sync-dolibarr`, { headers: { Authorization: `Bearer ${token}` } }),
                ]);
                setMessage(clientsRes.data.message);
                getAllClients();

                // Si un client est déjà sélectionné en mode "Réel", on rafraîchit aussi sa liste de projets affichée
                if (formik.values.client && isRealProject) {
                  const client = clients.find((c) => c._id === formik.values.client);
                  if (client?.dolibarr_id) {
                    fetchProjects(client.dolibarr_id);
                  }
                }
            } catch (err) {
                setError("Erreur de synchro");
            } finally {
                setLoading(false);
            }
        }}
        sx={{ mt: 1, border: `1px solid ${colorOrange}`, color: "black", fontSize: '0.7rem' }}
      >
        Synchroniser Dolibarr
      </Button>
    </Box>
  );
};

export default TurnoverInfosFields;